import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { trackAppcuesEvent } from "@expert/monitoring";
import { Flex } from "@mantine/core";
import { useEffect } from "react";
import { useVerifiedCustomerStore } from "@/customer-info";
import { useIVRInfo } from "@/customer-info/api";
import { CustomerVerificationFlowV3, useVerificationFlowStore } from "@/customer-panel";
import { CallIntroFlow } from "@/customer-panel/components/call-intro-flow/CallIntroFlow";
import { useCallIntroStore } from "@/customer-panel/components/call-intro-flow/store/CallIntroStore";
import { isIVRHandoffStep } from "@/customer-panel/components/customer-verification/utils";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { SalesFlow } from "@/sales-tools";
import { useSessionId, useTypedMostRecentTask } from "@/sdk";
import { CustomerInsights } from "../customer-insights/CustomerInsights";
import { useDigitalCompetencyListener } from "../customer-insights/digital-competency";
import { useTaskMdn } from "../customer-verification/hooks/useTaskMdn";
import classes from "./CustomerPanel.module.css";
import { CustomerProfile } from "./CustomerProfile";

const logger = getLogger({ module: "CustomerPanel" });

export function CustomerPanel() {
    const { isChat, voiceTask, chatTask } = useTypedMostRecentTask();
    const sessionId = useSessionId();
    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const callIntroStep = useCallIntroStore((state) => state.currentStep);
    const callIntroStepHistory = useCallIntroStore((state) => state.stepHistory);

    const isCustomerFlowComplete = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );
    const {
        features: { isEnhancedCustomerSearchEnabled },
        blocklistedNumbers,
        blocklistedPrefixes,
    } = usePartnerConfig();

    const {
        features: { isPreviousSessionSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();

    useDigitalCompetencyListener();

    const ivrInfo = useIVRInfo();

    const taskMdn = useTaskMdn({
        blocklistedNumbers,
        blocklistedPrefixes,
    });

    // Reset the offer status received flag when the component mounts
    useEffect(() => {
        trackAppcuesEvent("CustomerPanelVisible", {});
    }, []);

    // TODO: Move this out of use effect
    useEffect(() => {
        const isFlowComplete =
            callIntroStep === "IntroFlowCompleted" || stepHistory.at(stepHistory.length - 1) === "VerificationComplete";
        if (isFlowComplete !== isCustomerFlowComplete) {
            useVerifiedCustomerStore.getState().setIsCustomerVerificationComplete(sessionId, isFlowComplete);
        }
    }, [stepHistory, callIntroStep, isCustomerFlowComplete, sessionId]);

    if (!chatTask && !voiceTask) {
        logger.error("Customer Panel | Unable to find most recent task, customer panel will not display");
        return undefined;
    }

    const isEditing =
        !isCustomerFlowComplete &&
        (stepHistory.includes("VerificationComplete") || callIntroStepHistory.includes("IntroFlowCompleted"));

    const shouldShowCustomerProfile =
        isChat ||
        isCustomerFlowComplete ||
        isEditing ||
        !isIVRHandoffStep({
            callIntroStep,
            stepHistory,
            isEnhancedCustomerSearchEnabled,
            ivrInfo,
        });

    return (
        <Flex className={classes.customerPanel} data-testid="customer-panel">
            <Flex className={classes.responsivePanel}>
                {shouldShowCustomerProfile && (
                    <Flex className={classes.topSection}>
                        <CustomerProfile />
                    </Flex>
                )}
                {!isCustomerFlowComplete ? (
                    isEnhancedCustomerSearchEnabled ? (
                        <CallIntroFlow />
                    ) : (
                        <CustomerVerificationFlowV3
                            mdnFromTask={taskMdn}
                            key={sessionId}
                            isPreviousSessionSummaryEnabled={isPreviousSessionSummaryEnabled}
                        />
                    )
                ) : (
                    <>
                        {!isChat && <CustomerInsights />}
                        {<SalesFlow />}
                    </>
                )}
            </Flex>
        </Flex>
    );
}
